import React, { useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";
import "./AboutHero.css";
import aboutHeroVideo from "../../../../assets/videos/slider.MOV";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackAboutHero = {
  titleLine1: "Building Iraq with trusted",
  titleLine2: "gypsum manufacturing",
  description:
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions - serving local and regional markets with a strong commitment to quality, sustainability, and long-term partnerships.",
  stat1End: 2,
  stat1Suffix: "",
  stat1Label: "Branches - Baghdad & Erbil",
  stat2End: 8,
  stat2Suffix: "+",
  stat2Label: "Product lines",
  stat3End: 13,
  stat3Suffix: "+",
  stat3Label: "Cities across Iraq",
};

const imageExtensionRegex = /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?.*)?$/i;
const videoExtensionRegex = /\.(mp4|mov|webm|avi|mkv|m4v)(\?.*)?$/i;
const videoHintRegex = /(video|clip|trailer|stream|m3u8)/i;
const imageHintRegex = /(image|img|photo|thumbnail|thumb|banner|logo|avatar|format=webp|format=jpg|format=jpeg|format=png)/i;

const getHeroMediaCandidate = (data) => {
  if (!data || typeof data !== "object") return "";
  const candidates = [
    data.introVideoMediaUrl,
    data.introVideoUrl,
    data.introVideoMedia,
    data.mediaUrl,
    data.introVideo,
    data.videoUrl,
    data.heroVideo,
    data.backgroundVideo,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "object") {
      const objectCandidate = candidate.mediaUrl || candidate.url || candidate.value || "";
      const resolvedObject = resolveMediaUrl(objectCandidate);
      if (resolvedObject) return resolvedObject;
      continue;
    }
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const detectHeroMediaType = (src) => {
  const value = String(src || "");
  if (!value) return "video";
  if (videoExtensionRegex.test(value)) return "video";
  if (imageExtensionRegex.test(value)) return "image";
  if (videoHintRegex.test(value)) return "video";
  if (imageHintRegex.test(value)) return "image";
  return "video";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const pickStatEnd = (payload, keys, fallback) => {
  for (const key of keys) {
    const raw = payload?.[key];
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
};

const AboutHero = ({ content }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const view = useMemo(() => {
    const fb = fallbackAboutHero;
    const useApi = hasSectionData(content);
    const p = useApi && content && typeof content === "object" ? content : {};

    const titleLine1 = pickString(p.titleLine1, p.heading) || (() => {
      const t = pickString(p.title);
      if (t.includes("\n")) return t.split("\n")[0].trim();
      return t ? t : fb.titleLine1;
    })();
    const titleLine2 = pickString(p.titleLine2, p.subheading) || (() => {
      const t = pickString(p.title);
      if (t.includes("\n")) return t.split("\n").slice(1).join(" ").trim();
      return fb.titleLine2;
    })();

    const description =
      pickString(p.description, p.body, p.text, p.summary, p.content) || fb.description;

    const stat1End = pickStatEnd(p, ["stat1End", "stat1Value", "stat1Count", "stat1"], fb.stat1End);
    const stat2End = pickStatEnd(p, ["stat2End", "stat2Value", "stat2Count", "stat2"], fb.stat2End);
    const stat3End = pickStatEnd(p, ["stat3End", "stat3Value", "stat3Count", "stat3"], fb.stat3End);

    const dynamicSrc = getHeroMediaCandidate(p);
    const mediaSrc = dynamicSrc || aboutHeroVideo;
    const mediaType = detectHeroMediaType(dynamicSrc);

    return {
      titleLine1: titleLine1 || fb.titleLine1,
      titleLine2: titleLine2 || fb.titleLine2,
      description,
      stat1End,
      stat1Suffix: pickString(p.stat1Suffix) || fb.stat1Suffix,
      stat1Label: pickString(p.stat1Label, p.stat1Text, p.stat1Description) || fb.stat1Label,
      stat2End,
      stat2Suffix: pickString(p.stat2Suffix) || fb.stat2Suffix,
      stat2Label: pickString(p.stat2Label, p.stat2Text, p.stat2Description) || fb.stat2Label,
      stat3End,
      stat3Suffix: pickString(p.stat3Suffix) || fb.stat3Suffix,
      stat3Label: pickString(p.stat3Label, p.stat3Text, p.stat3Description) || fb.stat3Label,
      mediaSrc,
      mediaType: dynamicSrc ? mediaType : "video",
    };
  }, [content]);

  useEffect(() => {
    if (view.mediaType !== "video") return;
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    const tryPlay = async () => {
      try {
        await video.play();
        if (!cancelled) setIsPlaying(true);
      } catch {
        if (!cancelled) setIsPlaying(false);
      }
    };

    tryPlay();
    return () => {
      cancelled = true;
    };
  }, [view.mediaType, view.mediaSrc]);

  const toggleVideoPlayback = async () => {
    if (!videoRef.current || view.mediaType !== "video") return;

    if (videoRef.current.paused) {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
      } catch (_error) {
        setIsPlaying(false);
      }
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <section className="aboutHero" aria-label="About hero">
      <div className="aboutHero-inner">
        <h1 className="aboutHero-title">
          {view.titleLine1} <br />
          <span>{view.titleLine2}</span>
        </h1>

        <p className="aboutHero-text">{view.description}</p>

        <div className="aboutHero-stats">
          <div className="aboutHero-stat">
            <h3>
              <CountUp end={view.stat1End} duration={2} enableScrollSpy scrollSpyOnce />
              {view.stat1Suffix}
            </h3>
            <p>{view.stat1Label}</p>
          </div>

          <div className="aboutHero-stat">
            <h3>
              <CountUp end={view.stat2End} duration={2} enableScrollSpy scrollSpyOnce />
              {view.stat2Suffix}
            </h3>
            <p>{view.stat2Label}</p>
          </div>

          <div className="aboutHero-stat">
            <h3>
              <CountUp end={view.stat3End} duration={2} enableScrollSpy scrollSpyOnce />
              {view.stat3Suffix}
            </h3>
            <p>{view.stat3Label}</p>
          </div>
        </div>
      </div>

      <div className="aboutHero-media" aria-label="About hero preview">
        {view.mediaType === "image" ? (
          <img className="aboutHero-video" src={view.mediaSrc} alt="" />
        ) : (
          <video
            ref={videoRef}
            className="aboutHero-video"
            src={view.mediaSrc}
            autoPlay
            muted
            playsInline
            loop
            preload="auto"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        )}
        {view.mediaType === "video" ? (
          <button
            type="button"
            className="aboutHero-playBtn"
            onClick={toggleVideoPlayback}
            aria-label={isPlaying ? "Pause company video" : "Play company video"}
          >
            <span className={`aboutHero-playIcon ${isPlaying ? "is-pause" : ""}`} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default AboutHero;
